// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import { extent, max, min } from 'd3-array';
import Zion from './utils/Zion';
import { linearAxis, timeAxis } from '../../../factory/axes';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import { stylePremium } from './utils/helpers';
import config from './config';
import graphic from './graphic';

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'Mar.',
    'Apr.',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

let chartData;
let rawData;
/** One object per calendar year, sorted ascending by year (for draw order). */
let seriesByYear;

const margins = {
  top: 20,
  right: 110,
  bottom: 25,
  left: 45,
};

const parser = d3.timeParse('%Y-%m-%d');

/** Earliest calendar year we ever show. */
const CLIP_START_YEAR = 2023;
/** How many calendar years before the latest stay in view once the clip no longer binds (latest year plus this many prior). */
const PRIOR_YEAR_COUNT = 3;

const CHART_BLUE = '#5194C3';
const CHART_YELLOW = '#F8C153';
const CHART_RED = '#C52622';
const CHART_TEAL = '#53A796';
const CHART_ORANGE = '#F18851';
const CHART_PURPLE = '#7C4EA5';
/** Prior years (oldest first); current year always uses {@link CHART_ORANGE}. */
const SERIES_COLORS_PRIOR = [CHART_BLUE, CHART_YELLOW, CHART_TEAL, CHART_RED, CHART_PURPLE];

function plotWithinYear(date) {
  return new Date(2000, date.getMonth(), date.getDate());
}

function buildYearSeries(rows) {
  const byYear = {};
  rows.forEach((d) => {
    const y = d.date.getFullYear();
    if (!byYear[y]) byYear[y] = [];
    byYear[y].push(d);
  });
  return Object.keys(byYear)
    .map(k => +k)
    .sort((a, b) => a - b)
    .map(year => ({
      year,
      values: byYear[year].sort((a, b) => a.date - b.date),
    }));
}

/** X-axis / callout: full words for March and April (avoid Mar./Apr. confusion). */
function formatAxisMonth(d) {
  const m = d.getMonth();
  if (m === 2) return 'March';
  if (m === 3) return 'April';
  return d3.timeFormat('%b')(d);
}

function formatMonthDayLongMarApr(date) {
  return `${formatAxisMonth(date)} ${d3.timeFormat('%-d, %Y')(date)}`;
}

/**
 * Nudges a set of label targets apart along Y so none sit closer than minGap,
 * while keeping the stack as close as possible to everyone's true value and
 * preserving top-to-bottom order. Adds a `labelY` property to each item;
 * leaves `y` (the true endpoint) untouched so callers can still draw an
 * accurate leader line back to it.
 */
function declutterY(items, minGap) {
  const sorted = items.slice().sort((a, b) => a.y - b.y);
  sorted.forEach((d) => { d.labelY = d.y; });

  // Forward pass: push a label down if it's crowding the one above it.
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i].labelY - sorted[i - 1].labelY < minGap) {
      sorted[i].labelY = sorted[i - 1].labelY + minGap;
    }
  }

  // Backward pass: if the forward pass ran the bottom label too far down,
  // pull the stack back up so it settles around the true values instead of
  // drifting toward the bottom of the chart.
  for (let i = sorted.length - 2; i >= 0; i -= 1) {
    if (sorted[i + 1].labelY - sorted[i].labelY < minGap) {
      sorted[i].labelY = sorted[i + 1].labelY - minGap;
    }
  }

  return sorted;
}

// let recessionData;
let minValue;
let maxValue;

let minDomain;
let maxDomain;

let lastData;
let lastUpdatedStr;
let latestMonth;

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      if (id === 'headline') return;
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;
const pTime = d3.timeParse('%Y-%m-%d');

stylePremium(CNNPolitics, CNNBusiness);

const rParser = d3.timeParse('%Y-%m-%d');

let dekText;

d3.json('https://ix.cnn.io/data/gas-prices/aaa_gas_prices_avg_national_diesel_five_years.json').then((data) => {
  // Check for URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const freezeDateParam = urlParams.get('date');
  const yearsParam = urlParams.get('years');
  const yearParam = urlParams.get('year');
  let freezeDate = null;
  let yearsLimit = null;
  let specificYear = null;

  // Parse freeze date parameter (format: ?date=YYYYMMDD)
  if (freezeDateParam && freezeDateParam.length === 8) {
    const year = parseInt(freezeDateParam.substring(0, 4));
    const month = parseInt(freezeDateParam.substring(4, 6)) - 1; // Month is 0-indexed
    const day = parseInt(freezeDateParam.substring(6, 8));
    freezeDate = new Date(year, month, day);
    console.log('Freezing chart at date:', freezeDate);
  }

  // Parse years parameter (format: ?years=1 or ?years=2, etc.)
  if (yearsParam) {
    yearsLimit = parseInt(yearsParam);
    console.log('Limiting chart to', yearsLimit, 'year(s)');
  }

  // Parse year parameter (format: ?year=2026)
  if (yearParam) {
    specificYear = parseInt(yearParam);
    console.log('Showing calendar year:', specificYear);
  }

  // Map and filter data, removing any invalid entries
  chartData = data.data
    .map(d => ({
      date: d3.timeParse('%Y-%m-%d')(d.date),
      value: +d.price,
    }))
    .filter(d => d.date && !isNaN(d.value) && d.value !== null);

  // If freeze date is set, filter data to only include dates up to freeze date
  if (freezeDate) {
    chartData = chartData.filter(d => d.date <= freezeDate);
  }

  // If specific year is set, filter to that calendar year
  if (specificYear) {
    const yearStart = new Date(specificYear, 0, 1);
    const yearEnd = new Date(specificYear, 11, 31, 23, 59, 59);
    chartData = chartData.filter(d => d.date >= yearStart && d.date <= yearEnd);
  }

  // If years limit is set, filter data to only include the specified number of years
  if (yearsLimit && yearsLimit > 0) {
    // Find the maximum date in the (possibly frozen) data
    const maxDate = d3.max(chartData, d => d.date);
    // Calculate the minimum date by subtracting the specified years
    const minDate = new Date(maxDate);
    minDate.setFullYear(minDate.getFullYear() - yearsLimit);
    // Filter to only include data within the year range
    chartData = chartData.filter(d => d.date >= minDate);
  }

  const dataMaxDate = d3.max(chartData, d => d.date);
  const dataMaxYear = dataMaxDate.getFullYear();
  const minYearToShow = Math.max(CLIP_START_YEAR, dataMaxYear - PRIOR_YEAR_COUNT);
  chartData = chartData.filter(d => d.date.getFullYear() >= minYearToShow);

  seriesByYear = buildYearSeries(chartData);

  // Validate we have data to display
  if (chartData.length === 0) {
    console.error('No valid diesel price data available');
    d3.select('#graphic').append('p')
      .style('text-align', 'center')
      .style('padding', '40px')
      .text('Data temporarily unavailable');
    return;
  }

  minDomain = new Date(2000, 0, 1);
  maxDomain = new Date(2000, 11, 31, 23, 59, 59, 999);

  // Floored at $3 so the axis reads in even 50-cent steps ($3, $3.50, $4...)
  // instead of whatever oddly-cut number the 5% padding lands on. Still
  // falls back to the padded value if a future price ever dips below $3,
  // so the floor never clips the line.
  minValue = Math.min(3, d3.min(chartData, d => d.value) * 0.95);
  maxValue = d3.max(chartData, d => d.value) * 1.05;

  const latestObsYear = d3.max(chartData, d => d.date).getFullYear();
  const currentYearSeries = seriesByYear.find(s => s.year === latestObsYear);
  lastData = currentYearSeries && currentYearSeries.values.length
    ? currentYearSeries.values[currentYearSeries.values.length - 1]
    : chartData.reduce((a, b) => (a.date > b.date ? a : b));

  lastUpdatedStr = data.metadata.lastUpdatedStr;
  latestMonth = d3.timeFormat('%B %Y')(lastData.date);

  // Create editorial dek with highlighted values
  let latestPrice; let dayChange; let
    yearChange;

  if (freezeDate) {
    // Calculate values from filtered data
    latestPrice = lastData.value;

    // Find yesterday's price
    const yesterday = new Date(lastData.date);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayData = chartData.find(d => d.date.getTime() === yesterday.getTime());
    dayChange = yesterdayData ? lastData.value - yesterdayData.value : 0;

    // Find year ago price
    const yearAgo = new Date(lastData.date);
    yearAgo.setFullYear(yearAgo.getFullYear() - 1);
    const yearAgoData = chartData.find(d => d.date.getTime() === yearAgo.getTime());
    yearChange = yearAgoData ? lastData.value - yearAgoData.value : 0;

    // Update last updated string to reflect freeze date
    lastUpdatedStr = `${formatMonthDayLongMarApr(lastData.date)} at 9 a.m. ET`;
  } else {
    // Use metadata from API
    latestPrice = data.metadata.latestValue;
    dayChange = data.metadata.trends.dayChange;
    yearChange = data.metadata.trends.yearChange;
  }

  // Day and year deltas read as one flowing clause rather than two full
  // sentences. When both moved the same direction, the direction word is
  // stated once up front rather than repeated ("up $0.01 from yesterday and
  // $2.81 from a year ago"); mixed directions or a flat day/year each keep
  // their own wording.
  const directionOf = (change) => {
    if (change > 0) return 'up';
    if (change < 0) return 'down';
    return null;
  };
  const dayDirection = directionOf(dayChange);
  const yearDirection = directionOf(yearChange);
  const dayAmount = `<span class="highlight-value">$${Math.abs(dayChange).toFixed(2)}</span>`;
  const yearAmount = `<span class="highlight-value">$${Math.abs(yearChange).toFixed(2)}</span>`;

  let changeClause;
  if (dayDirection && dayDirection === yearDirection) {
    changeClause = `${dayDirection} ${dayAmount} from yesterday and ${yearAmount} from a year ago`;
  } else {
    const dayClause = dayDirection ? `${dayDirection} ${dayAmount} from yesterday` : 'unchanged from yesterday';
    const yearClause = yearDirection ? `${yearDirection} ${yearAmount} from a year ago` : 'unchanged from a year ago';
    changeClause = `${dayClause} and ${yearClause}`;
  }

  dekText = `Diesel, which fuels many trucks, tractors, trains, boats and construction vehicles, averages <span class="highlight-value">$${latestPrice.toFixed(2)}</span> per gallon in the US, ${changeClause}.`;

  if (dekText && dekText !== '') {
    d3.select('h2.dek')
      .html(dekText);
  }

  d3.select('h1.headline')
    .text(`How ${latestObsYear} diesel prices compare with the past three years`);

  draw();
  window.onresize = draw;
});


function draw() {
  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const height = 400;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const yTicks = isMobile ? 5 : 6;

  const xScale = d3.scaleTime()
    .domain([minDomain, maxDomain])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0]);

  const allMonthTicks = [];
  for (let m = 0; m < 12; m += 1) {
    allMonthTicks.push(new Date(2000, m, 1));
  }
  const skipInterval = isMobile ? 3 : 1;
  const monthTicks = allMonthTicks.filter((d, i) => i === 0 || i === allMonthTicks.length - 1 || i % skipInterval === 0);

  const xAxis = d3.axisBottom()
    .scale(xScale)
    .tickValues(monthTicks)
    .tickFormat(formatAxisMonth);

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .ticks(yTicks)
    .tickSize(-chartWidth)
    .tickFormat(d => `$${d3.format('.2f')(d)}`);

  const path = d3.line()
    .curve(d3.curveLinear)
    .x(d => xScale(plotWithinYear(d.date)))
    .y(d => yScale(d.value));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  // Append zero line
  g.append('line')
    .attr('class', 'zero-line')
    .attr('x1', 0)
    .attr('x2', chartWidth)
    .attr('y1', yScale(minValue))
    .attr('y2', yScale(minValue))
    .attr('stroke', '#393939')
    .attr('stroke-width', 1);

  const lineLayer = g.append('g')
    .attr('class', 'lines');

  const baseLineWidth = window.innerWidth < 768 ? 1.5 : 2;
  const latestYear = seriesByYear.length ? seriesByYear[seriesByYear.length - 1].year : null;

  const lineColor = (year) => {
    if (latestYear != null && year === latestYear) return CHART_ORANGE;
    const olderYears = seriesByYear.map(s => s.year).filter(y => y !== latestYear);
    const idx = olderYears.indexOf(year);
    const i = idx < 0 ? 0 : idx;
    return SERIES_COLORS_PRIOR[Math.min(i, SERIES_COLORS_PRIOR.length - 1)];
  };

  seriesByYear.forEach((series) => {
    const strokeW = series.year === latestYear ? baseLineWidth + 0.5 : baseLineWidth;
    lineLayer.append('path')
      .datum(series.values)
      .attr('stroke', lineColor(series.year))
      .attr('stroke-width', strokeW)
      .attr('fill', 'none')
      .attr('d', path);
  });

  // Endpoint targets for every year except the current one (it gets its own
  // annotation below). Decluttered along Y so lines that end within a few
  // cents of each other - 2024 and 2025 tend to - don't produce overlapping
  // year labels.
  const priorYearLabels = seriesByYear
    .filter(series => !(lastData && series.year === lastData.date.getFullYear()))
    .map((series) => {
      const lastPt = series.values[series.values.length - 1];
      if (!lastPt) return null;
      return {
        year: series.year,
        color: lineColor(series.year),
        x: xScale(plotWithinYear(lastPt.date)),
        y: yScale(lastPt.value),
      };
    })
    .filter(Boolean);

  const labelMinGap = isMobile ? 13 : 15;
  declutterY(priorYearLabels, labelMinGap);

  priorYearLabels.forEach(({
    year, color, x, y, labelY,
  }) => {
    // A label nudged off its true value keeps a thin leader back to the dot
    // it actually belongs to, so it doesn't read as floating free-form text.
    if (Math.abs(labelY - y) > 0.5) {
      g.append('line')
        .attr('class', 'series-year-leader')
        .attr('x1', x)
        .attr('x2', x + 6)
        .attr('y1', y)
        .attr('y2', labelY)
        .attr('stroke', color)
        .attr('stroke-width', 1)
        .attr('opacity', 0.5);
    }

    g.append('text')
      .attr('class', 'series-year-label')
      .attr('x', x)
      .attr('y', labelY)
      .attr('dx', 8)
      .attr('dy', isMobile ? 3 : 4)
      .attr('fill', '#333')
      .attr('font-size', 12)
      .style('font-family', 'sans-serif')
      .text(String(year));
  });

  d3.select('.grid.y .domain').remove();

  // Only add annotation if we have valid last data point
  if (lastData && lastData.date && !isNaN(lastData.value)) {
    const annotation = g.append('g')
      .attr('class', 'annotation')
      .attr(
        'transform',
        `translate(${[xScale(plotWithinYear(lastData.date)), yScale(lastData.value)]})`,
      );

    // Live endpoint pulse — the one cue that signals this number updates on
    // its own. A filled disc at the dot's own radius, behind the dot, so it
    // starts under the solid circle with nothing to clear.
    annotation.append('circle')
      .attr('class', 'pulse')
      .attr('r', '4')
      .attr('fill', lineColor(lastData.date.getFullYear()));

    annotation.append('circle')
      .attr('r', '4')
      .attr('fill', lineColor(lastData.date.getFullYear()))
      .attr('stroke', '#fff')
      .attr('stroke-width', 2);

    annotation.append('text')
      .attr('dx', '10px')
      .attr('class', 'annotation-date')
      .text(formatMonthDayLongMarApr(lastData.date));

    annotation.append('text')
      .attr('dx', '10px')
      .attr('class', 'annotation-value')
      .text(`$${d3.format('.2f')(lastData.value)}`)
      .attr('dy', '1.2em');
  }

  // Hover tooltip: reveals every year's price for the calendar day under the
  // cursor, since the four lines share one Jan-Dec axis. Hit targets come from
  // the longest series (a full prior year has an entry for every day) so the
  // grid spans the whole width even on days the current year hasn't reached.
  const longestSeries = seriesByYear.reduce(
    (longest, series) => (series.values.length > longest.values.length ? series : longest),
    seriesByYear[0],
  );

  const dayKey = d3.timeFormat('%m-%d');

  const seriesByDayKey = seriesByYear.map(series => ({
    year: series.year,
    color: lineColor(series.year),
    map: new Map(series.values.map(v => [dayKey(v.date), v])),
  }));

  const calendarDays = longestSeries.values.map(v => ({
    date: plotWithinYear(v.date),
    key: dayKey(v.date),
  }));

  const tooltip = container.append('div')
    .attr('class', 'tooltip')
    .style('display', 'none');

  const hoverLayer = g.append('g')
    .attr('class', 'hover-layer')
    .style('display', 'none');

  hoverLayer.append('line')
    .attr('class', 'hover-line')
    .attr('y1', 0)
    .attr('y2', chartHeight);

  const hoverDots = hoverLayer.selectAll('.hover-dot')
    .data(seriesByDayKey)
    .enter()
    .append('circle')
    .attr('class', 'hover-dot')
    .attr('r', 4)
    .attr('fill', d => d.color)
    .attr('stroke', '#fff')
    .attr('stroke-width', 1.5)
    .style('display', 'none');

  // Each calendar day's hit target spans the midpoints to its neighbors, so
  // hovering anywhere near a day's column reveals it - same strategy as the
  // jobs-monthly-simple-average and unemployment-rate tooltips.
  const hoverTargets = calendarDays.map((d, i) => {
    const left = i === 0
      ? 0
      : (xScale(calendarDays[i - 1].date) + xScale(d.date)) / 2;
    const right = i === calendarDays.length - 1
      ? chartWidth
      : (xScale(d.date) + xScale(calendarDays[i + 1].date)) / 2;
    return { ...d, left, right };
  });

  g.append('g')
    .attr('class', 'hover-targets')
    .selectAll('rect')
    .data(hoverTargets)
    .enter()
    .append('rect')
    .attr('x', d => d.left)
    .attr('width', d => Math.max(0, d.right - d.left))
    .attr('y', 0)
    .attr('height', chartHeight)
    .attr('fill', 'transparent')
    .on('mouseover', () => {
      tooltip.style('display', 'block');
      hoverLayer.style('display', null);
    })
    .on('mousemove', (d) => {
      const x = xScale(d.date);
      hoverLayer.select('.hover-line').attr('x1', x).attr('x2', x);

      hoverDots
        .style('display', s => (s.map.has(d.key) ? null : 'none'))
        .attr('cx', x)
        .attr('cy', s => (s.map.has(d.key) ? yScale(s.map.get(d.key).value) : 0));

      // Only years that actually reported a price on this calendar day get a
      // row, ordered top to bottom by value so the list reads the same way
      // the lines stack at this x position.
      const rows = seriesByDayKey
        .map(s => ({ year: s.year, color: s.color, point: s.map.get(d.key) }))
        .filter(r => r.point)
        .sort((a, b) => b.point.value - a.point.value);

      tooltip.html(`
        <div class="tooltip-date">${formatAxisMonth(d.date)} ${d3.timeFormat('%-d')(d.date)}</div>
        ${rows.map(r => `
          <div class="tooltip-row${r.year === latestYear ? ' is-current' : ''}">
            <span class="tooltip-swatch" style="background:${r.color}"></span>
            <span class="tooltip-year">${r.year}</span>
            <span class="tooltip-value">$${d3.format('.2f')(r.point.value)}</span>
          </div>
        `).join('')}
      `);

      const [mx, my] = d3.mouse(container.node());

      // Flip to the left of the cursor when there isn't room to the right,
      // so days near the right edge don't run the tooltip off the chart.
      const tooltipWidth = tooltip.node().offsetWidth;
      const left = (mx + 14 + tooltipWidth > initialWidth)
        ? mx - tooltipWidth - 14
        : mx + 14;

      tooltip
        .style('left', `${left}px`)
        .style('top', `${Math.max(0, my - 50)}px`);
    })
    .on('mouseout', () => {
      tooltip.style('display', 'none');
      hoverLayer.style('display', 'none');
    });

  // Append the current date as a timestamp below the chart
  const currentDateString = `Notes: Last updated ${lastUpdatedStr}.`;

  const timestamp = container.append('div')
    .attr('class', 'footnote')
    .text(currentDateString);

  window.pymChild.sendHeight();
}
